import React, { useState, useEffect } from "react";
import Parse from "../parseClient";
import "./Banner.css";
import { X } from "lucide-react";
import { useSwipeDown } from "../hooks/useSwipeDown";
import { isIOS, isAndroid, isNative, isWeb } from "../utils/platform";
import * as amplitude from '@amplitude/unified';

// Helper function to safely track Amplitude events
const trackBannerEvent = (eventName, eventProperties = {}) => {
  try {
    // Check if amplitude.track exists and is a function
    if (amplitude && typeof amplitude.track === 'function') {
      amplitude.track(eventName, eventProperties);
      console.log(`[Banner] Amplitude event tracked: ${eventName}`, eventProperties);
    } else {
      console.warn(`[Banner] Amplitude.track is not available. Event: ${eventName}`, eventProperties);
    }
  } catch (amplitudeError) {
    console.warn(`[Banner] Failed to track event ${eventName}:`, amplitudeError, eventProperties);
  }
};

const Banner = ({ numbersPlayed, langString = "en", currentUser = null, isGameFinished = false, onShareScore = null, onOpenCreateCustomWordHit = null, onOpenBattleMenu = null }) => {
  const [bannerData, setBannerData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const fetchBannerSettings = async () => {
      // Don't show banner if user is marked as possible cheater
      // Note: If currentUser is null/undefined (not logged in), banner will still be shown
      if (currentUser && currentUser.get("possibleCheater") === true) {
        setBannerData(null);
        setIsVisible(false);
        return;
      }
      
      if (!Parse || !Parse.Object) {
        console.warn("Parse is not ready");
        return;
      }

      try {
        const Settings = Parse.Object.extend("Settings");
        
        // Query for both "banner" and "bannerAfterPlay" types
        const query1 = new Parse.Query(Settings);
        query1.equalTo("type", "banner");
        query1.equalTo("bannerIsActive", true);
        
        const query2 = new Parse.Query(Settings);
        query2.equalTo("type", "bannerAfterPlay");
        query2.equalTo("bannerIsActive", true);
        
        // Combine queries using Parse.Query.or
        const combinedQuery = Parse.Query.or(query1, query2);

        // Get all active banners
        const results = await combinedQuery.find();
        
        if (results.length === 0) {
          setBannerData(null);
          setIsVisible(false);
          return;
        }

        // Get current date for filtering
        const now = new Date();
        const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Filter banners that are within date range and have valid text
        const validBanners = results.filter((result) => {
          const bannerType = result.get("type");
          
          // On web: hide banner/bannerAfterPlay if mayShowOnWeb/mustShowOnWeb is false or undefined
          if (isWeb() && !result.get("mayShowOnWeb") && !result.get("mustShowOnWeb")) {
            return false;
          }
          
          // For "bannerAfterPlay" type, only show if game is finished
          if (bannerType === "bannerAfterPlay" && !isGameFinished) {
            return false;
          }
          
          // For "banner" type, show normally (no game finished requirement)
          
          // Get startDate and endDate
          const startDate = result.get("startDate");
          const endDate = result.get("endDate");
          
          // Check if current date is within the date range
          // If startDate or endDate is provided, banner MUST be within range
          if (startDate || endDate) {
            // Handle Parse Date objects or regular Date objects
            const start = startDate ? (startDate instanceof Date ? startDate : new Date(startDate)) : null;
            const end = endDate ? (endDate instanceof Date ? endDate : new Date(endDate)) : null;
            
            // Validate dates
            if (start && isNaN(start.getTime())) {
              console.warn("Invalid startDate for banner:", startDate);
            }
            if (end && isNaN(end.getTime())) {
              console.warn("Invalid endDate for banner:", endDate);
            }
            
            // Set time to start of day for accurate comparison (UTC to avoid timezone issues)
            const startStartOfDay = start && !isNaN(start.getTime()) 
              ? new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) 
              : null;
            const endStartOfDay = end && !isNaN(end.getTime()) 
              ? new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate())) 
              : null;
            
            // Banner is invalid if before startDate
            if (startStartOfDay) {
              const nowUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
              if (nowUTC < startStartOfDay) {
                return false;
              }
            }
            // Banner is invalid if on or after endDate (endDate is exclusive - banner should not show on endDate)
            if (endStartOfDay) {
              const nowUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
              if (nowUTC >= endStartOfDay) {
                return false;
              }
            }
          }
          
          // Check if banner has text for current language
          const bannerText = langString === "nl" 
            ? result.get("bannerTextNL") 
            : result.get("bannerText");

          // Only allow banners that have non-empty text for the current language
          return typeof bannerText === "string" && bannerText.trim().length > 0;
        });

        if (validBanners.length === 0) {
          setBannerData(null);
          setIsVisible(false);
          return;
        }

        // Sort banners: first by priority (higher = more important), then by createdAt (newest first)
        // If priority doesn't exist, treat it as 0
        validBanners.sort((a, b) => {
          const priorityA = a.get("priority") ?? 0;
          const priorityB = b.get("priority") ?? 0;
          
          // First sort by priority (descending)
          if (priorityA !== priorityB) {
            return priorityB - priorityA;
          }
          
          // If priorities are equal, sort by createdAt (newest first)
          const createdAtA = a.get("createdAt")?.getTime() ?? 0;
          const createdAtB = b.get("createdAt")?.getTime() ?? 0;
          return createdAtB - createdAtA;
        });

        // Use the first (highest priority/newest) banner
        const result = validBanners[0];
        
        // Get the appropriate banner text based on language
        const bannerTextRaw = langString === "nl" 
          ? result.get("bannerTextNL") 
          : result.get("bannerText");
        const bannerText = typeof bannerTextRaw === "string" ? bannerTextRaw.trim() : "";
        const minimumNumbersPlayed = result.get("minimumNumbersPlayed") ?? 0;
        const maximumNumbersPlayed = result.get("maximumNumbersPlayed");
        const secondsToShow = result.get("secondsToShow");
        const color = result.get("color") || "blue";
        const fontSizeString = result.get("fontSize");
        // Convert fontSize string to number, default to 1.5rem if not provided
        const fontSize = fontSizeString ? parseFloat(fontSizeString) : 1.5;

        // Get the appropriate share text based on language
        const bannerShareText = langString === "nl" 
          ? result.get("bannerShareTextNL") 
          : result.get("bannerShareText");
        
        // Check if user is within the numbersPlayed range
        const meetsMinimum = numbersPlayed >= minimumNumbersPlayed;
        const meetsMaximum = maximumNumbersPlayed === null || maximumNumbersPlayed === undefined || numbersPlayed <= maximumNumbersPlayed;
        
        // Check if user meets the minimum numbersPlayed requirement and doesn't exceed maximum
        if (bannerText && meetsMinimum && meetsMaximum) {
          setBannerData({
            text: bannerText,
            shareText: bannerShareText || "",
            secondsToShow: secondsToShow,
            color: color,
            fontSize: fontSize,
          });
          setIsVisible(true);
        } else {
          // Hide banner if conditions are not met
          setIsVisible(false);
          setBannerData(null);
        }
      } catch (error) {
        console.error("Error fetching banner settings:", error);
      }
    };

    fetchBannerSettings();
  }, [numbersPlayed, langString, currentUser, isGameFinished]); // Check when numbersPlayed, langString, currentUser, or isGameFinished changes

  useEffect(() => {
    if (!isVisible || !bannerData || isDismissed || isClosing) return;

    const secondsToShow = bannerData.secondsToShow;
    
    // If secondsToShow is set and > 0, auto-close after that time
    if (secondsToShow && secondsToShow > 0) {
      const timer = setTimeout(() => {
        setIsClosing(true);
        setTimeout(() => {
          setIsVisible(false);
          setIsDismissed(true);
        }, 300); // Wait for animation to complete
      }, secondsToShow * 1000);

      return () => clearTimeout(timer);
    }
  }, [isVisible, bannerData, isDismissed, isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsDismissed(true);
    }, 300); // Wait for animation to complete
  };

  // Handle tap on top of banner to close (but not if clicking on links)
  const handleBannerClick = (e) => {
    // Only close if clicking on the banner itself, not on links or interactive elements
    const target = e.target;
    const isClickableElement = target.closest('a') || 
                               target.closest('.banner-shareable-text') || 
                               target.closest('.banner-reviewable-text') ||
                               target.closest('.banner-smartfriends-text') ||
                               target.closest('.banner-share-score-text') ||
                               target.closest('.banner-create-custom-wordhit-text') ||
                               target.closest('.banner-battle-menu-text') ||
                               target.closest('.banner-close');
    
    // Check if click is in the top area (top 40px of banner)
    const bannerContainer = e.currentTarget;
    const clickY = e.clientY - bannerContainer.getBoundingClientRect().top;
    const topClickableArea = 40; // Top 40px is clickable to close
    
    if (!isClickableElement && clickY <= topClickableArea) {
      handleClose();
    }
  };

  // Swipe down to close functionality
  const {
    modalRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useSwipeDown(handleClose, 80); // Lower threshold for banner (80px instead of 100px)

  // Handle share when clicking on text between # and #
  const handleShareClick = async (shareText) => {
    if (!shareText) {
      console.warn("No share text available");
      return;
    }

    try {
      if (navigator.share) {
        // Use native share dialog (works on web and iOS)
        await navigator.share({
          text: shareText,
        });
        // Optionally copy to clipboard after successful share (silently)
        try {
          await navigator.clipboard.writeText(shareText);
        } catch (clipboardError) {
          // Ignore clipboard errors - share was successful
        }
      } else {
        // Fallback: copy to clipboard if share is not available
        await navigator.clipboard.writeText(shareText);
        const message = langString === "nl"
          ? "Deze tekst is gekopieerd naar je klembord, je kunt hem nu delen!"
          : "This text has been copied to your clipboard, you can now share it!";
        alert(message);
      }
    } catch (shareError) {
      // If share fails (user cancelled, etc.), fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareText);
        const message = langString === "nl"
          ? "Deze tekst is gekopieerd naar je klembord, je kunt hem nu delen!"
          : "This text has been copied to your clipboard, you can now share it!";
        alert(message);
      } catch (clipboardError) {
        // Both share and clipboard failed
        console.warn("Share and clipboard both failed", shareError, clipboardError);
      }
    }
  };

  // Handle App Store/Play Store review when clicking on text between $ and $ (iOS and Android)
  const handleReviewClick = async () => {
    let reviewUrl;
    
    if (isIOS()) {
      const appStoreId = '6755532039';
      reviewUrl = `https://apps.apple.com/app/id${appStoreId}?action=write-review`;
      console.log("[Banner Review] Opening App Store review page:", reviewUrl);
    } else if (isAndroid()) {
      const packageName = 'com.wordhit.app';
      reviewUrl = `https://play.google.com/store/apps/details?id=${packageName}`;
      console.log("[Banner Review] Opening Play Store review page:", reviewUrl);
    } else {
      console.log("[Banner Review] Not on iOS or Android, review link not available");
      return;
    }
    
    // Use Capacitor's Browser plugin if available (most reliable for native apps)
    if (typeof window !== "undefined" && window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Browser) {
      try {
        await window.Capacitor.Plugins.Browser.open({ url: reviewUrl });
        console.log("[Banner Review] ✅ Opened via Browser plugin");
        return;
      } catch (error) {
        console.error("[Banner Review] Browser.open failed:", error);
      }
    }
    
    // Fallback: Create a hidden anchor and click it
    try {
      const link = document.createElement('a');
      link.href = reviewUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log("[Banner Review] Opened via anchor click (fallback)");
    } catch (error) {
      console.error("[Banner Review] Failed to open review link:", error);
    }
  };

  // Handle share score when clicking on text between ^ and ^
  const handleShareScoreClick = async () => {
    if (!onShareScore || typeof onShareScore !== 'function') {
      console.warn("onShareScore function not provided");
      return;
    }
    
    try {
      // Track Amplitude event
      trackBannerEvent("banner_url_clicked", {
        linkType: "shareScore"
      });
      
      // Call the shareScore function
      await onShareScore();
    } catch (error) {
      console.error("Error calling shareScore:", error);
    }
  };

  // Handle create custom wordhit when clicking on text between ± and ±
  const handleCreateCustomWordHitClick = () => {
    if (!onOpenCreateCustomWordHit || typeof onOpenCreateCustomWordHit !== 'function') {
      console.warn("onOpenCreateCustomWordHit function not provided");
      return;
    }
    
    try {
      // Track Amplitude event
      trackBannerEvent("banner_url_clicked", {
        linkType: "createCustomWordHit"
      });
      
      // Call the function to open the modal
      onOpenCreateCustomWordHit();
    } catch (error) {
      console.error("Error opening create custom wordhit modal:", error);
    }
  };

  // Handle battle menu when clicking on text between ~ and ~
  const handleBattleMenuClick = () => {
    if (!onOpenBattleMenu || typeof onOpenBattleMenu !== 'function') {
      console.warn("onOpenBattleMenu function not provided");
      return;
    }
    
    try {
      trackBannerEvent("banner_url_clicked", {
        linkType: "battleMenu"
      });
      onOpenBattleMenu();
    } catch (error) {
      console.error("Error opening battle menu:", error);
    }
  };

  // Handle smartfriends link when clicking on text between § and §
  const handleSmartFriendsClick = async () => {
    const smartFriendsUrl = 'https://smartfriends.app.link';
    
    console.log("[Banner SmartFriends] Opening smartfriends link:", smartFriendsUrl);
    
    // Use Capacitor's Browser plugin if available (most reliable for iOS)
    if (typeof window !== "undefined" && window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Browser) {
      try {
        await window.Capacitor.Plugins.Browser.open({ url: smartFriendsUrl });
        console.log("[Banner SmartFriends] ✅ Opened via Browser plugin");
        return;
      } catch (error) {
        console.error("[Banner SmartFriends] Browser.open failed:", error);
      }
    }
    
    // Fallback: Use window.open
    try {
      window.open(smartFriendsUrl, '_blank', 'noopener,noreferrer');
      console.log("[Banner SmartFriends] Opened via window.open");
    } catch (error) {
      console.error("[Banner SmartFriends] Failed to open link:", error);
    }
  };

  if (!isVisible || !bannerData || isDismissed) {
    return null;
  }

  // Split text by \n and add blank lines between
  const formatBannerText = (text) => {
    if (!text) return '';
    return text.split('\\n').join('\n\n');
  };

  // Convert text with links, shareable text (#...#), review text ($...$), smartfriends text (§...§), share score text (^...^), create custom wordhit text (±...±), battle menu text (~...~) to clickable elements
  const renderTextWithLinks = (text) => {
    if (!text) return '';
    
    const formattedText = formatBannerText(text);
    const shareText = bannerData?.shareText || "";
    
    // Combined regex pattern - matches URLs, text between # and #, text between $ and $, text between § and §, text between ^ and ^, text between ± and ±, text between ~ and ~
    // Pattern: #...# (non-greedy), $...$ (non-greedy), §...§ (non-greedy), ^...^ (non-greedy), ±...± (non-greedy), ~...~ (non-greedy), or URLs
    // Note: ± is U+00B1, we need to escape it properly in regex
    const combinedRegex = /(#.*?#|\$.*?\$|§.*?§|\^.*?\^|±.*?±|~.*?~|https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    
    // Split text by URLs, shareable text, and review text, create array of text and clickable elements
    const parts = [];
    let lastIndex = 0;
    let match;
    let keyCounter = 0;
    
    while ((match = combinedRegex.exec(formattedText)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(formattedText.substring(lastIndex, match.index));
      }
      
      const matchedText = match[0];
      
      // Check if it's a shareable text (starts and ends with #)
      if (matchedText.startsWith('#') && matchedText.endsWith('#') && matchedText.length > 2) {
        // Remove the # characters to get the display text
        const displayText = matchedText.slice(1, -1);
        
        parts.push(
          <span
            key={`share-${keyCounter++}`}
            onClick={(e) => {
              e.stopPropagation();
              // Track Amplitude event
              trackBannerEvent("banner_url_clicked", {
                linkType: "share"
              });
              handleShareClick(shareText);
            }}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            className="banner-shareable-text"
          >
            {displayText}
          </span>
        );
      } 
      // Check if it's a review text (starts and ends with $)
      else if (matchedText.startsWith('$') && matchedText.endsWith('$') && matchedText.length > 2) {
        // Remove the $ characters to get the display text
        const displayText = matchedText.slice(1, -1);
        const isNativeDevice = isIOS() || isAndroid();
        
        // Make it clickable on iOS and Android, on web just show the text
        if (isNativeDevice) {
          parts.push(
            <span
              key={`review-${keyCounter++}`}
              onClick={(e) => {
                e.stopPropagation();
                // Track Amplitude event
                trackBannerEvent("banner_url_clicked", {
                  linkType: "review"
                });
                handleReviewClick();
              }}
              style={{
                color: 'inherit',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              className="banner-reviewable-text"
            >
              {displayText}
            </span>
          );
        } else {
          // On web, just show the text without click functionality
          parts.push(
            <span
              key={`review-${keyCounter++}`}
              style={{
                color: 'inherit',
              }}
            >
              {displayText}
            </span>
          );
        }
      } 
      // Check if it's a smartfriends text (starts and ends with §)
      else if (matchedText.startsWith('§') && matchedText.endsWith('§') && matchedText.length > 2) {
        // Remove the § characters to get the display text
        const displayText = matchedText.slice(1, -1);
        
        parts.push(
          <span
            key={`smartfriends-${keyCounter++}`}
            onClick={(e) => {
              e.stopPropagation();
              // Track Amplitude event
              trackBannerEvent("banner_url_clicked", {
                linkType: "smartfriends"
              });
              handleSmartFriendsClick();
            }}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            className="banner-smartfriends-text"
          >
            {displayText}
          </span>
        );
      }
      // Check if it's a share score text (starts and ends with ^)
      else if (matchedText.startsWith('^') && matchedText.endsWith('^') && matchedText.length > 2) {
        // Remove the ^ characters to get the display text
        const displayText = matchedText.slice(1, -1);
        
        parts.push(
          <span
            key={`share-score-${keyCounter++}`}
            onClick={(e) => {
              e.stopPropagation();
              handleShareScoreClick();
            }}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            className="banner-share-score-text"
          >
            {displayText}
          </span>
        );
      }
      // Check if it's a create custom wordhit text (starts and ends with ±)
      else if (matchedText.startsWith('±') && matchedText.endsWith('±') && matchedText.length > 2) {
        // Remove the ± characters to get the display text
        const displayText = matchedText.slice(1, -1);
        
        parts.push(
          <span
            key={`create-custom-wordhit-${keyCounter++}`}
            onClick={(e) => {
              e.stopPropagation();
              handleCreateCustomWordHitClick();
            }}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            className="banner-create-custom-wordhit-text"
          >
            {displayText}
          </span>
        );
      }
      // Check if it's a battle menu text (starts and ends with ~)
      else if (matchedText.startsWith('~') && matchedText.endsWith('~') && matchedText.length > 2) {
        const displayText = matchedText.slice(1, -1);
        
        parts.push(
          <span
            key={`battle-menu-${keyCounter++}`}
            onClick={(e) => {
              e.stopPropagation();
              handleBattleMenuClick();
            }}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            className="banner-battle-menu-text"
          >
            {displayText}
          </span>
        );
      }
      else {
        // It's a URL
        let url = matchedText;
        // Add http:// if it starts with www.
        if (url.startsWith('www.')) {
          url = 'https://' + url;
        }
        
        parts.push(
          <a
            key={`url-${keyCounter++}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // Track Amplitude event
              trackBannerEvent("banner_url_clicked", {
                url: url,
                linkType: "url"
              });
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {matchedText}
          </a>
        );
      }
      
      lastIndex = match.index + matchedText.length;
    }
    
    // Add remaining text after last match
    if (lastIndex < formattedText.length) {
      parts.push(formattedText.substring(lastIndex));
    }
    
    // If no matches found, return formatted text as is
    if (parts.length === 0) {
      return formattedText;
    }
    
    return parts;
  };

  // Map color string to CSS variable
  const getBackgroundColor = (color) => {
    const colorMap = {
      red: 'var(--sf-red)',
      green: 'var(--sf-green)',
      blue: 'var(--sf-blue)',
      yellow: 'var(--sf-yellow)',
    };
    return colorMap[color.toLowerCase()] || colorMap.blue;
  };

  // Get text color based on background color (sf-blue for yellow, otherwise sf-bg)
  const getTextColor = (color) => {
    return color?.toLowerCase() === 'yellow' ? 'var(--sf-blue)' : 'var(--sf-bg)';
  };

  const backgroundColor = bannerData?.color ? getBackgroundColor(bannerData.color) : 'var(--sf-blue)';
  const textColor = bannerData?.color ? getTextColor(bannerData.color) : 'var(--sf-bg)';

  return (
    <div 
      ref={modalRef}
      className={`banner-container ${isClosing ? 'closing' : ''}`}
      style={{ backgroundColor: backgroundColor }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleBannerClick}
    >
      <button 
        className="banner-close" 
        onClick={handleClose} 
        aria-label="Close banner"
        style={{ color: textColor }}
      >
        <X size={20} />
      </button>
      <div className="banner-content">
        <span 
          className="banner-text" 
          style={{ 
            color: textColor,
            fontSize: `${bannerData.fontSize || 1.5}rem`
          }}
        >
          {renderTextWithLinks(bannerData.text)}
        </span>
      </div>
    </div>
  );
};

export default Banner;

